import { Box } from "@mui/material";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";

type Props = {
  title: string;
  /** current − original; can be negative. */
  deviationAmount: number;
  /** The original (planned) value, which scales the bar. */
  baselineValue: number;
  unit: string;
  /** The current value shown top-right. */
  displayValue: number;
};

/**
 * The app's `DeviationLinearGraphWithContainer`: a centred zero line with a
 * bar growing right for a surplus or left for a deficit, scaled so the full
 * half-width equals the baseline. Widths are percentages here, so no layout
 * measurement is needed.
 */
const DeviationLinearGraph = ({
  title,
  deviationAmount,
  baselineValue,
  unit,
  displayValue,
}: Props) => {
  const theme: Theme = useTheme();
  const baseline = baselineValue === 0 ? 1 : Math.abs(baselineValue);
  const widthPercent = Math.min(
    50,
    (Math.abs(deviationAmount) / (baseline * 2)) * 100
  );
  const isSurplus = deviationAmount > 0;
  const current = Math.abs(Number(displayValue.toFixed(0)));
  const original = Math.abs(Number(baselineValue.toFixed(0)));

  return (
    <Box sx={styles.container}>
      <Box sx={styles.numbers}>
        <Box sx={styles.title(theme)}>{title}</Box>
        <Box sx={styles.sum(theme)}>
          {current}
          <Box component="span" sx={styles.unit(theme)}>
            {" "}
            {unit}
          </Box>
        </Box>
      </Box>
      <Box sx={styles.track(theme)}>
        <Box sx={styles.bar(theme, widthPercent, isSurplus)} />
        <Box sx={styles.zeroPoint(theme)} />
      </Box>
      <Box sx={styles.original(theme)}>
        {original} {unit}
      </Box>
    </Box>
  );
};

export default DeviationLinearGraph;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    marginBottom: "2px",
  },
  numbers: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: "8px",
  },
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "24px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  sum: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
    lineHeight: "24px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  unit: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  track: (theme: Theme) => ({
    position: "relative" as "relative",
    height: "24px",
    display: "flex",
    alignItems: "center",
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      height: "6px",
      borderRadius: "3px",
      backgroundColor: theme.colorTokens.P[10],
    },
  }),
  bar: (theme: Theme, widthPercent: number, isSurplus: boolean) => ({
    position: "absolute" as "absolute",
    height: "6px",
    width: `${widthPercent}%`,
    left: isSurplus ? "50%" : `${50 - widthPercent}%`,
    backgroundColor: theme.colors_ds.PRIMARY_GRAPH_MAIN,
    borderRadius: isSurplus ? "0 3px 3px 0" : "3px 0 0 3px",
  }),
  zeroPoint: (theme: Theme) => ({
    position: "absolute" as "absolute",
    left: "calc(50% - 1px)",
    width: "2px",
    height: "24px",
    borderRadius: "1px",
    backgroundColor: theme.colors_ds.PRIMARY_GRAPH_MAIN,
  }),
  original: (theme: Theme) => ({
    textAlign: "center" as "center",
    marginTop: "4px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "24px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
};
